import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, Info } from "lucide-react";
import { TourCard } from "../components/site/TourCard";
import { useFetch } from "../lib/hooks";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";

const DURATIONS = { "": "Any duration", short: "Up to 6 days", mid: "7–9 days", long: "10+ days" };
const DUR_RANGE = { short: [null, 6], mid: [7, 9], long: [10, null] };

export default function GroupTours() {
  useSeo({ title: "Group Tours — Spiti, Ladakh & Char Dham Yatras | Wandervibe Journeys", description: "Browse Wandervibe group tours: Spiti Valley and Ladakh road trips, Kedarnath Yatra, Do Dham Yatra and Char Dham Yatra.", path: "/group-tours" });
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const destination = params.get("destination") || "";
  const category = params.get("category") || "";
  const duration = params.get("duration") || "";
  const budget = params.get("budget") || "";
  const sort = params.get("sort") || "recommended";
  const month = params.get("month");
  const travelers = params.get("travelers");
  const [tours, setTours] = useState(null);
  const { data: all } = useFetch("/tours?type=group");
  const { data: cats } = useFetch("/categories");

  const setParam = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p, { replace: true }); };

  useEffect(() => {
    const p = new URLSearchParams({ type: "group", sort });
    if (destination) p.set("destination", destination);
    if (category) p.set("category", category);
    if (params.get("q")) p.set("q", params.get("q"));
    if (duration) { const [a, b] = DUR_RANGE[duration]; if (a) p.set("min_days", a); if (b) p.set("max_days", b); }
    if (budget) p.set("max_price", budget);
    api.get(`/tours?${p.toString()}`).then((r) => setTours(r.data)).catch(() => setTours([]));
  }, [params, destination, category, duration, budget, sort]);

  const dests = useMemo(() => [...new Set((all || []).map((t) => t.destination))], [all]);
  const groupCats = (cats || []).filter((c) => c.type === "group");
  const hasPrices = (all || []).some((t) => t.price);
  const active = destination || category || duration || budget || params.get("q");

  return (
    <>
      <section className="relative overflow-hidden bg-[#171717] text-white">
        <img src="/images/ladakhflags.jpg" alt="Prayer flags above a Ladakh lake" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="container-x relative py-20 md:py-28">
          <p className="eyebrow">Group Tours</p>
          <h1 className="mt-3 max-w-2xl text-4xl sm:text-5xl lg:text-6xl">Journeys better shared</h1>
          <p className="mt-5 max-w-xl text-base text-white/85 md:text-lg">Himalayan road trips and spiritual yatras with fellow travellers.</p>
        </div>
      </section>
      <section className="container-x py-12 md:py-16">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5" data-testid="group-tours-filters">
          <form onSubmit={(e) => { e.preventDefault(); setParam("q", q.trim()); }} className="flex gap-2">
            <div className="relative flex-1"><Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tours, places or routes" className="field !pl-11" data-testid="group-tours-search-input" /></div>
            <button className="btn-brand !rounded-xl" data-testid="group-tours-search-btn">Search</button>
          </form>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <select className="field" value={destination} onChange={(e) => setParam("destination", e.target.value)} data-testid="filter-destination-select">
              <option value="">All destinations</option>{dests.map((d) => <option key={d}>{d}</option>)}
            </select>
            <select className="field" value={category} onChange={(e) => setParam("category", e.target.value)} data-testid="filter-trip-type-select">
              <option value="">All trip types</option>{groupCats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <select className="field" value={duration} onChange={(e) => setParam("duration", e.target.value)} data-testid="filter-duration-select">
              {Object.entries(DURATIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {hasPrices && (
              <select className="field" value={budget} onChange={(e) => setParam("budget", e.target.value)} data-testid="filter-budget-select">
                <option value="">Any budget</option>{[15000, 25000, 40000, 60000].map((b) => <option key={b} value={b}>Up to ₹{b.toLocaleString("en-IN")}</option>)}
              </select>
            )}
            <select className="field" value={sort} onChange={(e) => setParam("sort", e.target.value)} data-testid="sort-select">
              <option value="recommended">Sort: Recommended</option>
              <option value="duration_asc">Duration: Shortest first</option>
              <option value="duration_desc">Duration: Longest first</option>
              {hasPrices && <option value="price_asc">Price: Low to high</option>}
              <option value="name">Name: A–Z</option>
            </select>
          </div>
          {active && (
            <button onClick={() => { setQ(""); setParams({}, { replace: true }); }} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#FD691B]" data-testid="filters-clear-btn"><X className="h-4 w-4" />Clear filters</button>
          )}
        </div>
        {(month || travelers) && (
          <p className="mt-5 flex items-start gap-2 rounded-xl bg-[#FFF3EC] px-4 py-3 text-sm text-[#171717]" data-testid="search-context-note">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#FD691B]" />
            {month ? `Travelling in ${month}` : "Your search"}{travelers ? ` · ${travelers} traveller(s)` : ""} — departure dates and availability are confirmed by our team when you enquire.
          </p>
        )}
        <p className="mt-8 flex items-center gap-2 text-sm text-slate-500" data-testid="group-tours-count"><SlidersHorizontal className="h-4 w-4" />{tours ? `${tours.length} tour${tours.length === 1 ? "" : "s"} found` : "Loading tours…"}</p>
        <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {(tours || []).map((t) => <TourCard key={t.slug} tour={t} />)}
        </div>
        {tours && !tours.length && <p className="py-16 text-center text-slate-500" data-testid="group-tours-empty">No tours match these filters yet. Try clearing a filter or talk to us on WhatsApp.</p>}
      </section>
    </>
  );
}
